import { CandidateType } from '../../../mirage/types';

export type CandidateDetails = CandidateType & {
  email?: string;
  phone?: string;
  coverLetter?: string;
  createdAt?: string;
  updatedAt?: string;
};

function parseDate(value: any): Date {
  if (!value) return new Date();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function normalizeCvSummary(value: any) {
  if (!value) return undefined;
  const highlights = Array.isArray(value?.highlights) ? value.highlights : undefined;

  return {
    text: value?.text ?? '',
    highlights: highlights?.length ? highlights : undefined,
    updatedAt: value?.updatedAt ?? value?.updated_at,
    sourceDocumentId: value?.sourceDocumentId ?? value?.source_document_id,
  };
}

/**
 * Normalizes ATS candidate records coming from the API into the flat `CandidateType`
 * shape used throughout the FE components.
 *
 * The API currently returns Mongo records in the shape:
 *   { system: { id, company }, details: { ...fields }, audit: { dateCreated, dateUpdated } }
 */
export function normalizeCandidate(raw: any): CandidateType & { email?: string; phone?: string } {
  const system = raw?.system || {};
  const details = raw?.details || raw || {};

  const resume = details?.resume || raw?.resume || {};
  const skills = details?.skills ?? raw?.skills ?? [];
  const cvSummary = normalizeCvSummary(details?.cvSummary ?? raw?.cvSummary);

  const date =
    details?.date ??
    raw?.date ??
    raw?.audit?.dateCreated ??
    raw?.audit?.date_created ??
    null;

  return {
    id: system?.id ?? details?.id ?? raw?.id ?? '',
    name: details?.name ?? raw?.name ?? '',
    status: details?.status ?? raw?.status ?? '',
    resume: {
      title: resume?.title ?? '',
      url: resume?.url ?? '',
      documentId: resume?.documentId ?? resume?.document_id,
      uploadId: resume?.uploadId ?? resume?.upload_id,
    },
    position: details?.position ?? raw?.position ?? '',
    location: details?.location ?? raw?.location ?? '',
    description: details?.description ?? raw?.description ?? '',
    skills: Array.isArray(skills) ? skills : [],
    date: parseDate(date),
    department: details?.department ?? raw?.department ?? '',
    email: details?.email ?? raw?.email ?? '',
    phone: details?.phone ?? raw?.phone ?? '',
    cvSummary,
  };
}

export function normalizeCandidateDetails(raw: any): CandidateDetails {
  const system = raw?.system || {};
  const details = raw?.details || raw || {};
  const audit = raw?.audit || {};

  return {
    ...normalizeCandidate(raw),
    id: system?.id ?? details?.id ?? raw?.id ?? '',
    coverLetter: details?.coverLetter ?? raw?.coverLetter,
    createdAt: audit?.dateCreated ?? audit?.date_created,
    updatedAt: audit?.dateUpdated ?? audit?.date_updated,
  };
}

export function normalizeCandidates(rawList: any[]): CandidateType[] {
  return (rawList || []).map(normalizeCandidate);
}

